import { useId, useRef, useState, useEffect } from 'react'
import { Info } from 'lucide-react'
import { METRICS } from '../lib/metrics'
import type { MetricKey } from '../lib/metrics'
import { computeTipShift } from '../lib/tipPosition'
import { useIsMobile } from '../hooks/useMediaQuery'
import './InfoTip.css'

interface InfoTipProps {
  metric: MetricKey
}

// A trigger near the left/right edge of the viewport would otherwise get a
// tip centered right off the edge — a symmetric CSS-only clamp can't fix
// this because the trigger's distance to the viewport edge isn't knowable
// in pure CSS. Measured on hover/focus and written as an inline custom
// property the CSS reads (falling back to the old centered `-50%` before
// JS has run, e.g. first paint or a non-DOM render context).
function updateTipShift(wrapperEl: HTMLSpanElement | null) {
  if (!wrapperEl) return
  try {
    const rect = wrapperEl.getBoundingClientRect()
    const wrapperCenterX = rect.left + rect.width / 2
    const viewportWidth = window.innerWidth
    const tipWidth = Math.min(240, 0.78 * viewportWidth) // mirrors InfoTip.css's max-width
    const shift = computeTipShift(wrapperCenterX, viewportWidth, tipWidth)
    wrapperEl.style.setProperty('--tip-shift', `${shift}px`)
  } catch {
    // jsdom / snapshot-render contexts without real layout (getBoundingClientRect
    // returning zeros, or no `window`) — leave the CSS default in place.
  }
}

/**
 * Accessible metric-definition tip (spec §7): a ⓘ button whose definition
 * shows on hover AND keyboard focus. Reveal is CSS-only (:hover/:focus-within);
 * iOS tap-to-focus is unreliable (F14), so a fallback onClick handler toggles
 * an `is-open` class that CSS also displays. The horizontal position is
 * JS-assisted (see updateTipShift above).
 */
export function InfoTip({ metric }: InfoTipProps) {
  const def = METRICS[metric]
  const uid = useId()
  const tooltipId = `info-tip-${metric}-${uid}`
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const isMobile = useIsMobile()

  // Handle outside-click to close the tip when it's open.
  useEffect(() => {
    if (!isOpen) return
    const handleDocumentClick = (event: MouseEvent | TouchEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    try {
      document.addEventListener('click', handleDocumentClick)
      document.addEventListener('touchstart', handleDocumentClick)
      return () => {
        document.removeEventListener('click', handleDocumentClick)
        document.removeEventListener('touchstart', handleDocumentClick)
      }
    } catch {
      // SSR or jsdom context without document — skip listener attachment.
    }
  }, [isOpen])

  const handleTriggerClick = () => {
    // Click-to-pin is a touch-only fallback (F14: iOS tap-to-focus is
    // unreliable). At >=768px, :hover/:focus-within already reveal the
    // tip, so a click there would only pin it open past mouseout with no
    // clean way to dismiss it — a no-op keeps desktop click behavior
    // exactly as it was before `is-open` existed.
    if (!isMobile) return
    setIsOpen(!isOpen)
    updateTipShift(wrapperRef.current)
  }

  return (
    <span
      className={`info-tip ${isOpen ? 'is-open' : ''}`}
      ref={wrapperRef}
      onMouseEnter={() => updateTipShift(wrapperRef.current)}
      onFocus={() => updateTipShift(wrapperRef.current)}
    >
      <button
        type="button"
        className="info-tip-trigger focus-ring"
        aria-label={`What is ${def.label}?`}
        aria-describedby={tooltipId}
        aria-expanded={isOpen}
        title={def.definition}
        onClick={handleTriggerClick}
      >
        <Info size={12} aria-hidden="true" />
      </button>
      <span id={tooltipId} role="tooltip" className="info-tip-body">
        <strong>{def.label}</strong> — {def.definition}
      </span>
    </span>
  )
}
